import { Role } from './enums.js';

/**
 * Explicit permission strings (spec section 14: "Hiding a button in React is not
 * authorization"). Every protected admin route checks one of these server-side.
 */
export const Permission = {
  DASHBOARD_VIEW: 'dashboard:view',

  PRODUCT_VIEW: 'product:view',
  PRODUCT_WRITE: 'product:write',
  PRODUCT_DELETE: 'product:delete',

  CATEGORY_VIEW: 'category:view',
  CATEGORY_WRITE: 'category:write',

  COLLECTION_VIEW: 'collection:view',
  COLLECTION_WRITE: 'collection:write',

  INVENTORY_VIEW: 'inventory:view',
  INVENTORY_ADJUST: 'inventory:adjust',
  INVENTORY_RESTOCK: 'inventory:restock',

  ORDER_VIEW: 'order:view',
  ORDER_WRITE: 'order:write',
  ORDER_REFUND: 'order:refund',

  CUSTOMER_VIEW: 'customer:view',

  // Help & Support tickets: read the inbox, answer customers (and close/reopen), route tickets to people and departments
  SUPPORT_VIEW: 'support:view',
  SUPPORT_REPLY: 'support:reply',
  SUPPORT_ASSIGN: 'support:assign',

  MEDIA_VIEW: 'media:view',
  MEDIA_WRITE: 'media:write',
  MEDIA_DELETE: 'media:delete',

  BANNER_VIEW: 'banner:view',
  BANNER_WRITE: 'banner:write',

  CONTENT_VIEW: 'content:view',
  CONTENT_WRITE: 'content:write',

  PROMOTION_VIEW: 'promotion:view',
  PROMOTION_WRITE: 'promotion:write',

  SETTINGS_VIEW: 'settings:view',
  SETTINGS_WRITE: 'settings:write',

  USER_VIEW: 'user:view',
  USER_WRITE: 'user:write',

  AUDIT_VIEW: 'audit:view',
  REPORT_VIEW: 'report:view',
} as const;
export type Permission = (typeof Permission)[keyof typeof Permission];

const ALL_PERMISSIONS = Object.values(Permission);

const READ_ONLY: Permission[] = [
  Permission.DASHBOARD_VIEW,
  Permission.PRODUCT_VIEW,
  Permission.CATEGORY_VIEW,
  Permission.COLLECTION_VIEW,
  Permission.INVENTORY_VIEW,
  Permission.ORDER_VIEW,
  Permission.CUSTOMER_VIEW,
  Permission.SUPPORT_VIEW,
  Permission.MEDIA_VIEW,
  Permission.BANNER_VIEW,
  Permission.CONTENT_VIEW,
  Permission.PROMOTION_VIEW,
  Permission.REPORT_VIEW,
];

export const rolePermissions: Record<Role, Permission[]> = {
  [Role.SUPER_ADMIN]: ALL_PERMISSIONS,
  [Role.CATALOG_MANAGER]: [
    ...READ_ONLY,
    Permission.PRODUCT_WRITE,
    Permission.PRODUCT_DELETE,
    Permission.CATEGORY_WRITE,
    Permission.COLLECTION_WRITE,
    Permission.MEDIA_WRITE,
  ],
  [Role.INVENTORY_MANAGER]: [
    ...READ_ONLY,
    Permission.INVENTORY_ADJUST,
    Permission.INVENTORY_RESTOCK,
  ],
  [Role.ORDER_MANAGER]: [
    ...READ_ONLY,
    Permission.ORDER_WRITE,
    Permission.ORDER_REFUND,
    Permission.SUPPORT_REPLY,
    Permission.SUPPORT_ASSIGN,
  ],
  [Role.CONTENT_MANAGER]: [
    ...READ_ONLY,
    Permission.BANNER_WRITE,
    Permission.CONTENT_WRITE,
    Permission.PROMOTION_WRITE,
    Permission.MEDIA_WRITE,
  ],
  [Role.VIEWER_ANALYST]: [...READ_ONLY, Permission.AUDIT_VIEW],
};
